import type { CSSProperties } from 'react';
import type { CustomContent, CustomTextContent, CommentsContent, ScopeOfWorkContent, TermsConditionsContent } from '@/blocks/domain/types';
import type { RendererProps } from '../../domain/RendererContract';
import { ProseBodySlot, ProseTitleSlot } from './prose/ProseSlots';
import { blockFontFamilyCss, blockFontSizeCss, blockFontWeightCss } from '../../application/blockFontSize';
import { sanitizeProseHtml } from '../../application/sanitizeProseHtml';
import { chipUnboundTokensInHtml } from '../../application/unboundTokenHtml';
import { renderUnboundTokens } from './fields/unboundTokenDisplay';
import { useProseRenderMode } from './prose/useProseRenderMode';

type ProseKinds = ScopeOfWorkContent | CommentsContent | TermsConditionsContent | CustomTextContent | CustomContent;

interface Props extends RendererProps<ProseKinds> {}

/**
 * The prose block heading (Figma `be.terms-conditions.editor` 15:39 and `be.scope-of-work.editor` 11:69: bold,
 * sentence case, ~18px at 100% zoom, the text colour). `ProseBlockPdf` mirrors it at `pxToPt(18)`; keep the two in step.
 */
function labelStyle(accent: string): CSSProperties {
  return {
    fontSize: 18,
    fontWeight: 700,
    color: accent,
    marginBottom: 12
  };
}

/**
 * Prose blocks (LLAMS-610 DB-05). One markup for every surface — the export path's resolved,
 * sliced HTML. Two editing mounts swap only the body (and, in the block editor, the heading):
 *  - quote canvas (`QuoteCanvasEditingContext` prose slice): the pencil opens the host's editor in
 *    place of the body (`ProseQuoteBody`);
 *  - block editor (`EditableDocumentContext` on the editor surface): Heading and Text are selectable
 *    page elements (`ProseCanvasElements`).
 * With neither mounted the output is byte-identical to before (`ProseRender.export.snapshot.test`).
 */
function ProseRenderImpl(props: Props) {
  const { content, style } = props;
  const model = useProseRenderMode(props);
  if (model.hidden) return null;

  const label = labelStyle(model.accent);
  // Sink-level sanitise (LLAMS-610 X-06): memoised, and idempotent over the resolver's output.
  const sanitizedHtml = sanitizeProseHtml(model.visibleHtml);
  // Sample data off: the author's dynamic values stay visible as chips (D12) rather than vanishing.
  const safeHtml = model.keepTokens ? chipUnboundTokensInHtml(sanitizedHtml) : sanitizedHtml;
  const titleText = model.keepTokens ? (renderUnboundTokens(model.resolvedTitle) ?? model.resolvedTitle) : model.resolvedTitle;
  const staticBody = safeHtml ? <div dangerouslySetInnerHTML={{ __html: safeHtml }} /> : null;

  return (
    <article
      style={{
        minHeight: model.minHeight,
        paddingTop: style.paddingY,
        paddingBottom: style.paddingY,
        color: style.textColor,
        background: style.backgroundColor,
        fontSize: blockFontSizeCss(style),
        fontWeight: blockFontWeightCss(style),
        fontFamily: blockFontFamilyCss(style)
      }}
      aria-label={`block-prose-${content.kind}`}
    >
      <ProseTitleSlot model={model} labelStyle={label}>
        {model.showTitle ? <div style={label}>{titleText}</div> : null}
      </ProseTitleSlot>
      {model.showContinuedLabel ? <div style={label}>{titleText} (continued)</div> : null}
      <ProseBodySlot model={model} kind={content.kind}>
        {staticBody}
      </ProseBodySlot>
    </article>
  );
}

export const scopeOfWorkRenderer = { Component: ProseRenderImpl, flowBehavior: 'splittable' as const };
export const commentsRenderer = { Component: ProseRenderImpl, flowBehavior: 'splittable' as const };
export const termsRenderer = { Component: ProseRenderImpl, flowBehavior: 'splittable' as const };
export const customTextRenderer = { Component: ProseRenderImpl, flowBehavior: 'splittable' as const };
export const customRenderer = { Component: ProseRenderImpl, flowBehavior: 'splittable' as const };

export type { ScopeOfWorkContent, CommentsContent, TermsConditionsContent, CustomTextContent, CustomContent, ProseKinds };
